<script setup lang="ts">
import useNetworkStore from '~/store/network'
import FaqItem from '~/components/Networks/FaqItem.vue'

const networkStore = useNetworkStore()

defineEmits(['scrollToMap'])
</script>

<template>
    <div class="faq">
        <FaqItem with-border rounded-top>
            <template #question>
                <i18n-t keypath="networks.faq.item1.question" scope="global">
                    <template #name>
                        {{ networkStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer font-bold">
                    <i18n-t keypath="networks.faq.item1.answer1" scope="global">
                        <template #name>
                            {{ networkStore.name }}
                        </template>
                        <template #answer2>
                            {{
                                $t(
                                    'networks.faq.item1.answer2',
                                    networkStore.networkData
                                        ?.totalStationsCount || 0
                                )
                            }}
                        </template>
                        <template #answer3>
                            <span class="font-normal">
                                {{
                                    $t(
                                        'networks.faq.item1.answer3',
                                        networkStore.networkData
                                            ?.totalEvsesCount || 0
                                    )
                                }}
                            </span>
                        </template>
                        <template #answer4>
                            <span class="font-normal">
                                <i18n-t
                                    keypath="networks.faq.item1.answer4"
                                    scope="global"
                                >
                                    <template #chargemapMap>
                                        <a
                                            class="link"
                                            href="/map"
                                            :title="
                                                $t('networks.faq.chargemap_map')
                                            "
                                        >
                                            {{
                                                $t('networks.faq.chargemap_map')
                                            }}
                                        </a>
                                    </template>
                                </i18n-t>
                            </span>
                        </template>
                    </i18n-t>
                </span>
            </template>
        </FaqItem>
        <FaqItem with-border>
            <template #question>
                <i18n-t keypath="networks.faq.item2.question" scope="global">
                    <template #name>
                        {{ networkStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer">
                    <i18n-t
                        scope="global"
                        :keypath="`networks.faq.item2.answer_${networkStore.compatiblePrefix}`"
                    >
                        <template #name>
                            {{ networkStore.name }}
                        </template>
                        <template #chargemapPass>
                            <a
                                class="link"
                                href="/pass"
                                :title="$t('chargemap_pass')"
                            >
                                {{ $t('chargemap_pass') }}
                            </a>
                        </template>
                    </i18n-t>
                </span>
            </template>
        </FaqItem>
        <FaqItem with-border>
            <template #question>
                <i18n-t keypath="networks.faq.item3.question" scope="global">
                    <template #name>
                        {{ networkStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer">
                    <ol>
                        <li>
                            {{ $t('networks.faq.item3.answer1') }}
                            <a
                                class="link"
                                href="/mobile"
                                :title="$t('mobile.meta_title')"
                            >
                                {{ $t('networks.faq.item3.app') }}
                            </a>
                        </li>

                        <li>
                            <i18n-t
                                keypath="networks.faq.item3.answer2"
                                scope="global"
                            >
                                <template #name>
                                    {{ networkStore.name }}
                                </template>
                            </i18n-t>
                        </li>

                        <li>
                            {{ $t('networks.faq.item3.answer3') }}
                        </li>
                    </ol>
                </span>
            </template>
        </FaqItem>
        <FaqItem v-if="networkStore.isIonity" with-border>
            <template #question>
                <i18n-t keypath="networks.faq.item4.question" scope="global">
                    <template #name>
                        {{ networkStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer">
                    <i18n-t keypath="networks.faq.item4.answer" scope="global">
                        <template #subscribe>
                            <a
                                class="link"
                                href="/ionity-subscription"
                                :title="$t('networks.faq.subscribe')"
                            >
                                {{ $t('networks.faq.subscribe') }}
                            </a>
                        </template>
                    </i18n-t>
                </span>
            </template>
        </FaqItem>
        <FaqItem with-border>
            <template #question>
                <i18n-t keypath="networks.faq.item5.question" scope="global">
                    <template #name>
                        {{ networkStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer">
                    <i18n-t scope="global" keypath="networks.faq.item5.answer1">
                        <template #name>
                            <span class="font-bold">
                                {{ networkStore.name }}
                            </span>
                        </template>
                    </i18n-t>
                    <br />
                    <br />
                    {{ $t('networks.faq.item5.answer2') }}
                    <br />
                    <br />
                    <i18n-t scope="global" keypath="networks.faq.item5.answer3">
                        <template #name>
                            <span class="font-bold">
                                {{ networkStore.name }}
                            </span>
                        </template>
                    </i18n-t>
                    <br />
                    <br />
                    <i18n-t scope="global" keypath="networks.faq.item5.answer4">
                        <template #part1>
                            <span class="font-bold">
                                {{ $t('networks.faq.item5.answer4_1') }}
                            </span>
                        </template>
                        <template #part3>
                            <button
                                class="link"
                                type="button"
                                @click="$emit('scrollToMap')"
                            >
                                {{
                                    $t('networks.faq.network_map', {
                                        name: networkStore.name
                                    })
                                }}
                            </button>
                        </template>
                    </i18n-t>
                </span>
            </template>
        </FaqItem>
        <FaqItem with-border>
            <template #question>
                <i18n-t keypath="networks.faq.item6.question" scope="global">
                    <template #name>
                        {{ networkStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer">
                    <i18n-t
                        v-if="networkStore.isCompatible"
                        scope="global"
                        :keypath="`networks.faq.item6.answer_${networkStore.compatiblePrefix}`"
                    >
                        <template #name>
                            {{ networkStore.name }}
                        </template>
                        <template #networkMap>
                            <button
                                class="link"
                                type="button"
                                @click="$emit('scrollToMap')"
                            >
                                {{
                                    $t('networks.faq.network_map', {
                                        name: networkStore.name
                                    })
                                }}
                            </button>
                        </template>
                    </i18n-t>

                    <div v-else>
                        <i18n-t
                            scope="global"
                            :keypath="`networks.faq.item6.answer_${networkStore.compatiblePrefix}1`"
                        >
                            <template #name>
                                <span class="font-bold">
                                    {{ networkStore.name }}
                                </span>
                            </template>
                        </i18n-t>
                        <br />
                        <br />
                        <i18n-t
                            scope="global"
                            :keypath="`networks.faq.item6.answer_${networkStore.compatiblePrefix}2`"
                        >
                            <template #name>
                                <span class="font-bold">
                                    {{ networkStore.name }}
                                </span>
                            </template>
                        </i18n-t>
                    </div>
                </span>
            </template>
        </FaqItem>
        <FaqItem rounded-bottom>
            <template #question>
                <i18n-t keypath="networks.faq.item7.question" scope="global">
                    <template #name>
                        {{ networkStore.name }}
                    </template>
                </i18n-t>
            </template>
            <template #answer>
                <span class="answer">
                    <i18n-t scope="global" keypath="networks.faq.item7.answer1">
                        <template #part2>
                            <span class="font-bold">
                                {{ $t('networks.faq.item7.answer1_2') }}
                            </span>
                        </template>
                    </i18n-t>
                    <ol>
                        <li>
                            <i18n-t
                                scope="global"
                                :keypath="`networks.faq.item7.answer2_${networkStore.compatiblePrefix}`"
                            >
                                <template #pass>
                                    <a
                                        class="link"
                                        href="/pass"
                                        :title="
                                            $t('networks.faq.chargemap_pass')
                                        "
                                    >
                                        {{
                                            $t(
                                                'networks.faq.chargemap_pass_multi'
                                            )
                                        }}
                                    </a>
                                </template>
                            </i18n-t>
                        </li>
                        <li>
                            <i18n-t
                                scope="global"
                                keypath="networks.faq.item7.answer3"
                            >
                                <template #part2>
                                    <br />
                                    {{ $t('networks.faq.item7.answer3_2') }}
                                </template>
                            </i18n-t>
                        </li>
                        <li>
                            {{
                                $t(
                                    `networks.faq.item7.answer4_${networkStore.compatiblePrefix}`
                                )
                            }}
                        </li>
                        <li v-if="networkStore.isCompatible">
                            {{ $t('networks.faq.item7.answer5') }}
                        </li>
                    </ol>
                    <br />
                    <span class="font-bold">
                        <i18n-t
                            scope="global"
                            keypath="networks.faq.item7.answer6"
                        >
                            <template #name>
                                {{ networkStore.name }}
                            </template>
                        </i18n-t>
                    </span>
                    <br />
                    <a
                        class="link"
                        href="/pass/purchase/step1"
                        :title="$t('word.order_your_pass')"
                    >
                        {{ $t('word.order_your_pass') }}
                    </a>
                </span>
            </template>
        </FaqItem>
    </div>
</template>

<style lang="scss" scoped>
.faq {
    @apply w-full p-6 md:py-12 md:px-0 bg-sky-100 flex flex-col justify-center items-center md:gap-7 md:mt-12;

    .answer {
        @apply text-zinc-800 text-base font-inter leading-6 md:leading-relaxed;

        ol {
            @apply list-decimal list-inside;

            li {
                @apply mt-3;

                &::marker {
                    @apply font-bold;
                }
            }
        }
    }

    .link {
        @apply underline text-sky-500;
    }
}
</style>
